/**
 * @page /portal/vereinbarungen/:id
 * @description Kunden-Portal: Detail einer Vereinbarung (Phase 2 #931).
 *   Zeigt Inhalt, Bedingung, Quelle, Audit-Log und Chatter.
 *   Bei Status pending_customer: 3 Action-Buttons (Bestaetigen/Widersprechen/Anmerken).
 *   Magic-Link via ?token= URL-Parameter.
 * @api GET/POST /api/portal/commitments/:id, /audit-log, /chatter, /comment, /confirm, /reject
 * @roles portal_kunde
 * @i18n customerPortal.commitments
 */
import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Modal } from '../components/ui';
import { ArrowLeft, Download, CheckCircle2, XCircle, MessageSquare, FileText, Clock, AlertTriangle, User, Send } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_CONFIG = {
  pending_customer:  { bg: 'bg-amber-500/10',   text: 'text-amber-700 dark:text-amber-400',   border: 'border-amber-300 dark:border-amber-800',   label: 'customerPortal.commitments.status.pending' },
  active:            { bg: 'bg-emerald-500/10', text: 'text-emerald-700 dark:text-emerald-400', border: 'border-emerald-300 dark:border-emerald-800', label: 'customerPortal.commitments.status.active' },
  rejected_customer: { bg: 'bg-rose-500/10',    text: 'text-rose-700 dark:text-rose-400',     border: 'border-rose-300 dark:border-rose-800',     label: 'customerPortal.commitments.status.rejected' },
  expired:           { bg: 'bg-gray-500/10',    text: 'text-gray-700 dark:text-gray-400',     border: 'border-gray-300 dark:border-gray-700',     label: 'customerPortal.commitments.status.expired' },
  superseded:        { bg: 'bg-violet-500/10',  text: 'text-violet-700 dark:text-violet-400', border: 'border-violet-300 dark:border-violet-800', label: 'customerPortal.commitments.status.superseded' },
  terminated:        { bg: 'bg-rose-500/10',    text: 'text-rose-700 dark:text-rose-400',     border: 'border-rose-300 dark:border-rose-800',     label: 'customerPortal.commitments.status.terminated' },
};

function formatDate(v) {
  if (!v) return '\u2013';
  try {
    const s = typeof v === 'string' ? v.slice(0, 10) : v;
    return new Date(typeof s === 'string' ? s + 'T00:00:00' : s).toLocaleDateString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return String(v); }
}

function formatDateTime(v) {
  if (!v) return '\u2013';
  try {
    return new Date(v).toLocaleString('de-AT', { timeZone: 'Europe/Vienna', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return String(v); }
}

const AUDIT_ICONS = {
  created: FileText,
  approved_internal: CheckCircle2,
  internal_approved: CheckCircle2,
  customer_email_sent: Send,
  customer_email_resent: Send,
  customer_reminder_sent: Clock,
  activated_admin: CheckCircle2,
  customer_confirmed: CheckCircle2,
  customer_rejected: XCircle,
  customer_commented: MessageSquare,
  auto_expired: Clock,
  expired: Clock,
  terminated: XCircle,
  superseded: FileText,
};

const AUDIT_LABELS = {
  created: 'customerPortal.commitments.audit.created',
  approved_internal: 'customerPortal.commitments.audit.internalApproved',
  internal_approved: 'customerPortal.commitments.audit.internalApproved',
  customer_email_sent: 'customerPortal.commitments.audit.emailSent',
  customer_email_resent: 'customerPortal.commitments.audit.emailSent',
  customer_reminder_sent: 'customerPortal.commitments.audit.reminderSent',
  activated_admin: 'customerPortal.commitments.audit.confirmed',
  customer_confirmed: 'customerPortal.commitments.audit.confirmed',
  customer_rejected: 'customerPortal.commitments.audit.rejected',
  customer_commented: 'customerPortal.commitments.audit.commented',
  auto_expired: 'customerPortal.commitments.audit.expired',
  expired: 'customerPortal.commitments.audit.expired',
  terminated: 'customerPortal.commitments.audit.terminated',
  superseded: 'customerPortal.commitments.audit.superseded',
};

export default function PortalVereinbarungDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { t } = useLanguage();
  const [commitment, setCommitment] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [chatter, setChatter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmStep, setConfirmStep] = useState(1);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectGrund, setRejectGrund] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const chatterRef = useRef(null);
  const commentInputRef = useRef(null);

  const tokenSuffix = token ? `?token=${encodeURIComponent(token)}` : '';

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/portal/commitments/${id}${tokenSuffix}`, { credentials: 'include' }).then(r => r.ok ? r.json() : Promise.reject(r)),
      fetch(`${API}/api/portal/commitments/${id}/audit-log${tokenSuffix}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { audit_log: [] }),
      fetch(`${API}/api/portal/commitments/${id}/chatter${tokenSuffix}`, { credentials: 'include' }).then(r => r.ok ? r.json() : { activities: [] }),
    ]).then(([detail, audit, chat]) => {
      setCommitment(detail.commitment || detail);
      setAuditLog(Array.isArray(audit.audit_log) ? audit.audit_log : []);
      setChatter(Array.isArray(chat.activities) ? chat.activities : (Array.isArray(chat.messages) ? chat.messages : []));
    }).catch(() => {
      setError(t('customerPortal.commitments.loadError'));
    }).finally(() => setLoading(false));
  }, [id, tokenSuffix, t]);

  useEffect(() => { load(); }, [load]);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleConfirm() {
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/commitments/${id}/confirm${tokenSuffix}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'confirm_failed');
      }
      setShowConfirmModal(false);
      setConfirmStep(1);
      showToast(t('customerPortal.commitments.confirmSuccess'), 'success');
      load();
    } catch (e) {
      showToast(t('customerPortal.commitments.confirmError'), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReject() {
    if (rejectGrund.trim().length < 5) {
      showToast(t('customerPortal.commitments.rejectGrundRequired'), 'error');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/commitments/${id}/reject${tokenSuffix}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ grund: rejectGrund.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'reject_failed');
      }
      setShowRejectModal(false);
      setRejectGrund('');
      showToast(t('customerPortal.commitments.rejectSuccess'), 'success');
      load();
    } catch (e) {
      showToast(t('customerPortal.commitments.rejectError'), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleComment() {
    if (commentText.trim().length === 0) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/portal/commitments/${id}/comment${tokenSuffix}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: commentText.trim() }),
      });
      if (!res.ok) throw new Error('comment_failed');
      setCommentText('');
      showToast(t('customerPortal.commitments.commentSuccess'), 'success');
      load();
    } catch (e) {
      showToast(t('customerPortal.commitments.commentError'), 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function scrollToComment() {
    chatterRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setTimeout(() => commentInputRef.current?.focus(), 400);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin h-8 w-8 border-2 border-[#338edc] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error || !commitment) {
    return (
      <div className="p-6">
        <Link to="/portal/vereinbarungen" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-content mb-4">
          <ArrowLeft size={16} /> {t('customerPortal.commitments.backToList')}
        </Link>
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-card p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
          <p className="text-sm text-rose-700 dark:text-rose-300">{error || t('customerPortal.commitments.notFound')}</p>
        </div>
      </div>
    );
  }

  const c = commitment;
  const cfg = STATUS_CONFIG[c.status] || STATUS_CONFIG.expired;
  const isPending = c.status === 'pending_customer';
  const isActive = c.status === 'active';
  const isRejected = c.status === 'rejected_customer';

  return (
    <div className="p-6 space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 px-4 py-3 rounded-xl shadow-lg border ${
          toast.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300'
            : 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300'
        }`}>
          <div className="text-sm font-medium">{toast.msg}</div>
        </div>
      )}

      {/* Back */}
      <Link to="/portal/vereinbarungen" className="inline-flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-content">
        <ArrowLeft size={16} /> {t('customerPortal.commitments.backToList')}
      </Link>

      {/* Header */}
      <div className="bg-surface-card border border-line rounded-card p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-mono text-[var(--text-tertiary)]">{c.commitment_nr}</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
                {t(cfg.label)}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-content mb-1">{c.titel}</h1>
            {c.kategorie && <p className="text-sm text-[var(--text-secondary)]">{c.kategorie}</p>}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${API}/api/portal/commitments/${id}/pdf${tokenSuffix}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#338edc]/10 hover:bg-[#338edc]/20 text-[#338edc] text-xs font-medium transition-colors"
            >
              <Download size={13} />
              {t('customerPortal.commitments.downloadPdf')}
            </a>
          </div>
        </div>
      </div>

      {/* Status banners */}
      {isPending && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-card p-5">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-amber-900 dark:text-amber-200 mb-1">{t('customerPortal.commitments.bannerPendingTitle')}</h3>
              <p className="text-sm text-amber-800 dark:text-amber-300 mb-4">{t('customerPortal.commitments.bannerPendingMsg')}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => { setConfirmStep(1); setShowConfirmModal(true); }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
                >
                  <CheckCircle2 size={16} />
                  {t('customerPortal.commitments.actionConfirm')}
                </button>
                <button
                  onClick={() => setShowRejectModal(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition-colors"
                >
                  <XCircle size={16} />
                  {t('customerPortal.commitments.actionReject')}
                </button>
                <button
                  onClick={scrollToComment}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white dark:bg-surface-card border border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-sm font-medium transition-colors"
                >
                  <MessageSquare size={16} />
                  {t('customerPortal.commitments.actionComment')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isActive && (
        <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-card p-5">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-emerald-900 dark:text-emerald-200 mb-1">{t('customerPortal.commitments.bannerActiveTitle')}</h3>
              <p className="text-sm text-emerald-800 dark:text-emerald-300">
                {c.customer_bestaetigt_am ? t('customerPortal.commitments.bannerActiveMsg', { date: formatDateTime(c.customer_bestaetigt_am) }) : t('customerPortal.commitments.bannerActiveMsgGeneric')}
              </p>
            </div>
          </div>
        </div>
      )}

      {isRejected && (
        <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-card p-5">
          <div className="flex items-start gap-3">
            <XCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-rose-900 dark:text-rose-200 mb-1">{t('customerPortal.commitments.bannerRejectedTitle')}</h3>
              {c.customer_ablehnung_grund && (
                <div className="mt-2 p-3 rounded-lg bg-white dark:bg-surface-card border border-rose-200 dark:border-rose-800">
                  <div className="text-[10px] uppercase tracking-wider text-rose-600 dark:text-rose-400 font-semibold mb-1">{t('customerPortal.commitments.yourReason')}</div>
                  <p className="text-sm text-content whitespace-pre-wrap">{c.customer_ablehnung_grund}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Meta-Grid */}
      <div className="bg-surface-card border border-line rounded-card p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        <MetaField label={t('customerPortal.commitments.meta.gueltigAb')} value={formatDate(c.gueltig_ab)} />
        <MetaField label={t('customerPortal.commitments.meta.gueltigBis')} value={formatDate(c.gueltig_bis)} />
        <MetaField label={t('customerPortal.commitments.meta.erstelltAm')} value={formatDate(c.erstellt_am)} />
        <MetaField label={t('customerPortal.commitments.meta.versendetAm')} value={c.customer_email_sent_at ? formatDate(c.customer_email_sent_at) : '\u2013'} />
      </div>

      {/* Inhalt */}
      <Section title={t('customerPortal.commitments.section.content')} icon={FileText}>
        <div className="text-sm text-content whitespace-pre-wrap leading-relaxed">{c.inhalt}</div>
      </Section>

      {c.bedingung && (
        <Section title={t('customerPortal.commitments.section.condition')} icon={AlertTriangle}>
          <div className="text-sm text-content whitespace-pre-wrap leading-relaxed">{c.bedingung}</div>
        </Section>
      )}

      {(c.quelle_typ || c.quelle_notiz) && (
        <Section title={t('customerPortal.commitments.section.source')} icon={MessageSquare}>
          {c.quelle_typ && (
            <div className="text-xs text-[var(--text-secondary)] mb-2">
              <span className="font-semibold">{t('customerPortal.commitments.source.type')}:</span> {c.quelle_typ}
              {c.quelle_datum && ` \u00b7 ${formatDate(c.quelle_datum)}`}
            </div>
          )}
          {c.quelle_notiz && <div className="text-sm text-content whitespace-pre-wrap leading-relaxed">{c.quelle_notiz}</div>}
        </Section>
      )}

      {/* Audit-Log */}
      {auditLog.length > 0 && (
        <Section title={t('customerPortal.commitments.section.auditLog')} icon={Clock}>
          <div className="space-y-3">
            {auditLog.map((entry, i) => {
              const Icon = AUDIT_ICONS[entry.aktion] || FileText;
              const labelKey = AUDIT_LABELS[entry.aktion];
              return (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={13} className="text-[#338edc]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-content font-medium">{labelKey ? t(labelKey) : entry.aktion}</div>
                    <div className="text-xs text-[var(--text-tertiary)] mt-0.5">
                      {formatDateTime(entry.ausgefuehrt_am || entry.created_at)}
                      {entry.ausgefuehrt_von_name && ` \u00b7 ${entry.ausgefuehrt_von_name}`}
                    </div>
                    {entry.begruendung && <div className="text-xs text-[var(--text-secondary)] mt-1 whitespace-pre-wrap">{entry.begruendung}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* Chatter */}
      <div ref={chatterRef}>
        <Section title={t('customerPortal.commitments.section.chatter')} icon={MessageSquare}>
          <div className="space-y-3 mb-4">
            {chatter.length === 0 ? (
              <div className="text-sm text-[var(--text-tertiary)] italic py-4 text-center">{t('customerPortal.commitments.noMessages')}</div>
            ) : (
              chatter.map((m, i) => (
                <div key={i} className="flex items-start gap-3 text-sm">
                  <div className="w-7 h-7 rounded-full bg-[var(--text-tertiary)]/10 flex items-center justify-center flex-shrink-0">
                    <User size={13} className="text-[var(--text-secondary)]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-xs font-semibold text-content">{m.author_name || t('customerPortal.commitments.unknownAuthor')}</span>
                      <span className="text-[10px] text-[var(--text-tertiary)]">{formatDateTime(m.created_at)}</span>
                    </div>
                    <div className="text-sm text-content whitespace-pre-wrap">{m.body}</div>
                  </div>
                </div>
              ))
            )}
          </div>

          {!isRejected && (
            <div className="border-t border-line pt-4">
              <textarea
                ref={commentInputRef}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={t('customerPortal.commitments.commentPlaceholder')}
                rows={3}
                className="w-full px-3 py-2 rounded-lg bg-[var(--bg-inset)] border border-line text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-[#338edc] resize-none"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={handleComment}
                  disabled={submitting || commentText.trim().length === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#338edc] hover:bg-[#2570b8] disabled:bg-[#338edc]/50 text-white text-sm font-medium transition-colors"
                >
                  <Send size={14} />
                  {t('customerPortal.commitments.sendComment')}
                </button>
              </div>
            </div>
          )}
        </Section>
      </div>

      {/* Confirm Modal (Doppelbestätigung) */}
      <Modal
        open={showConfirmModal}
        onClose={() => { if (!submitting) { setShowConfirmModal(false); setConfirmStep(1); } }}
        title={confirmStep === 1 ? t('customerPortal.commitments.confirmModalTitle1') : t('customerPortal.commitments.confirmModalTitle2')}
        size="md"
      >
        {confirmStep === 1 ? (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
              <p className="text-sm text-emerald-800 dark:text-emerald-300">{t('customerPortal.commitments.confirmModalStep1Msg')}</p>
            </div>
            <div className="text-sm text-content">
              <strong>{c.commitment_nr}</strong> &mdash; {c.titel}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setShowConfirmModal(false); setConfirmStep(1); }}
                className="px-4 py-2 rounded-lg border border-line text-sm text-[var(--text-secondary)] hover:bg-surface-hover transition-colors"
              >
                {t('customerPortal.commitments.cancel')}
              </button>
              <button
                onClick={() => setConfirmStep(2)}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium transition-colors"
              >
                {t('customerPortal.commitments.next')}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-300">{t('customerPortal.commitments.confirmModalStep2Msg')}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setConfirmStep(1)}
                disabled={submitting}
                className="px-4 py-2 rounded-lg border border-line text-sm text-[var(--text-secondary)] hover:bg-surface-hover transition-colors"
              >
                {t('customerPortal.commitments.back')}
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
              >
                {submitting ? <span className="animate-spin h-4 w-4 border-2 border-white/40 border-t-white rounded-full" /> : <CheckCircle2 size={14} />}
                {t('customerPortal.commitments.confirmFinal')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal (Begründung-Pflicht) */}
      <Modal
        open={showRejectModal}
        onClose={() => { if (!submitting) { setShowRejectModal(false); setRejectGrund(''); } }}
        title={t('customerPortal.commitments.rejectModalTitle')}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-[var(--text-secondary)]">{t('customerPortal.commitments.rejectModalMsg')}</p>
          <div>
            <label className="block text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider mb-1">
              {t('customerPortal.commitments.rejectGrundLabel')} <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={rejectGrund}
              onChange={(e) => setRejectGrund(e.target.value)}
              placeholder={t('customerPortal.commitments.rejectGrundPlaceholder')}
              rows={5}
              className="w-full px-3 py-2 rounded-lg bg-[var(--bg-inset)] border border-line text-sm text-content placeholder:text-[var(--text-tertiary)] focus:outline-none focus:border-rose-500 resize-none"
            />
            <div className="text-[10px] text-[var(--text-tertiary)] mt-1">
              {rejectGrund.trim().length}/5 {t('customerPortal.commitments.minChars')}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => { setShowRejectModal(false); setRejectGrund(''); }}
              disabled={submitting}
              className="px-4 py-2 rounded-lg border border-line text-sm text-[var(--text-secondary)] hover:bg-surface-hover transition-colors"
            >
              {t('customerPortal.commitments.cancel')}
            </button>
            <button
              onClick={handleReject}
              disabled={submitting || rejectGrund.trim().length < 5}
              className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:bg-rose-600/50 text-white text-sm font-medium transition-colors inline-flex items-center gap-2"
            >
              {submitting ? <span className="animate-spin h-4 w-4 border-2 border-white/40 border-t-white rounded-full" /> : <XCircle size={14} />}
              {t('customerPortal.commitments.rejectFinal')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function MetaField({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] font-semibold mb-1">{label}</div>
      <div className="text-sm text-content font-medium">{value}</div>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-6">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon size={16} className="text-[#338edc]" />}
        <h2 className="text-base font-semibold text-content">{title}</h2>
      </div>
      {children}
    </div>
  );
}
